var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useMemo } from 'react';
import { Button } from '@parca/components';
import { useContainerDimensions } from '@parca/hooks';
import { selectQueryParam } from '@parca/utilities';
import DiffLegend from '../components/DiffLegend';
import { IcicleGraph } from './IcicleGraph';
var numberFormatter = new Intl.NumberFormat('en-US');
var ProfileIcicleGraph = function (_a) {
    var graph = _a.graph, total = _a.total, filtered = _a.filtered, curPath = _a.curPath, setNewCurPath = _a.setNewCurPath, sampleUnit = _a.sampleUnit, onContainerResize = _a.onContainerResize, navigateTo = _a.navigateTo, loading = _a.loading, setActionButtons = _a.setActionButtons;
    var compareMode = selectQueryParam('compare_a') === 'true' && selectQueryParam('compare_b') === 'true';
    var _b = useContainerDimensions(), ref = _b.ref, dimensions = _b.dimensions;
    useEffect(function () {
        if (dimensions === undefined)
            return;
        if (onContainerResize === undefined)
            return;
        onContainerResize(dimensions.width, dimensions.height);
    }, [dimensions, onContainerResize]);
    var _c = useMemo(function () {
        if (graph === undefined) {
            return ['0', '0', false, '0', '0', false, '0', '0'];
        }
        var trimmed = parseInt(graph.trimmed);
        var totalUnfiltered = total + filtered;
        // safeguard against division by zero
        var totalUnfilteredDivisor = totalUnfiltered > 0 ? totalUnfiltered : 1;
        return [
            numberFormatter.format(total),
            numberFormatter.format(totalUnfiltered),
            trimmed > 0,
            numberFormatter.format(trimmed),
            numberFormatter.format((trimmed * 100) / totalUnfilteredDivisor),
            filtered > 0,
            numberFormatter.format((total * 100) / totalUnfilteredDivisor),
        ];
    }, [graph, filtered, total]), totalFormatted = _c[0], totalUnfilteredFormatted = _c[1], isTrimmed = _c[2], trimmedFormatted = _c[3], trimmedPercentage = _c[4], isFiltered = _c[5], filteredPercentage = _c[6];
    useEffect(function () {
        if (setActionButtons === undefined) {
            return;
        }
        setActionButtons(_jsx(_Fragment, { children: _jsx(Button, __assign({ color: "neutral", onClick: function () { return setNewCurPath([]); }, disabled: curPath.length === 0, variant: "neutral" }, { children: "Reset View" })) }));
    }, [setNewCurPath, curPath, setActionButtons]);
    if (graph === undefined)
        return _jsx("div", { children: "no data..." });
    if (total === 0 && !loading)
        return _jsx(_Fragment, { children: "Profile has no samples" });
    if (isTrimmed) {
        console.info("Trimmed ".concat(trimmedFormatted, " (").concat(trimmedPercentage, "%) too small values."));
    }
    return (_jsxs("div", __assign({ className: "relative" }, { children: [compareMode && _jsx(DiffLegend, {}), _jsx("div", __assign({ ref: ref }, { children: _jsx(IcicleGraph, { width: dimensions === null || dimensions === void 0 ? void 0 : dimensions.width, graph: graph, total: total, filtered: filtered, curPath: curPath, setCurPath: setNewCurPath, sampleUnit: sampleUnit, navigateTo: navigateTo }) })), _jsxs("p", __assign({ className: "my-2 text-xs" }, { children: ["Showing ", totalFormatted, ' ', isFiltered ? (_jsxs("span", { children: ["(", filteredPercentage, "%) filtered of ", totalUnfilteredFormatted, ' '] })) : (_jsx(_Fragment, {})), "values.", ' '] }))] })));
};
export default ProfileIcicleGraph;
